import { useEffect, useLayoutEffect, useRef } from "react";
import usePreviousValue from "../../../../../common/ui/usePreviousValue";
import { useActiveDialog } from "../../../framework/connectors/active-dialog/useActiveDialog";

// A hook to manage responsive modals contained in Left/RightBar
// if a new dialog is launched responsive modals need to back out
// focus trap and zindex.  Normally you don't need to use this.
// Use the ReaderDialog component and this will be handled automatically

export function useDialogManager(open: boolean) {
  const wasOpen = usePreviousValue(open);
  const { setActiveDialog } = useActiveDialog();
  const elLastFocus = useRef<HTMLElement | null>();

  useLayoutEffect(() => {
    if (open && !wasOpen) {
      // with responsive modals a11y-dialog focus restore can get skewed
      // so save dialog launch control before things start to move
      elLastFocus.current = document.activeElement as HTMLElement;
    }
  }, [open, wasOpen]);

  useEffect(() => {
    if (open && !wasOpen) {
      // this will back out any responsive modals (i.e. right/left bar)
      setActiveDialog(true);
    } else if (!open && wasOpen) {
      // this will bring any responsive modals back
      setActiveDialog(false);
      // with combinations of deferred open and responsive modals
      // a11y-dialog might not have the correct dialog trigger control
      // and end up restoring the wrong one, we let it have a go and then
      // correct it if necessary
      setTimeout(() => {
        if (
          elLastFocus.current &&
          document.activeElement !== elLastFocus.current
        ) {
          elLastFocus.current?.focus();
        }
      }, 1);
    }
  }, [open, setActiveDialog, wasOpen]);
}
